import { Check, CreditCard, Printer } from 'lucide-react'

import { useHotelAvailability } from '@/components/HotelAvailabilityContext'
import { usePeriodsForReservation } from '@/shared/hooks/use-periods-for-reservation'
import { ReceiptPrintDialog } from '@/shared/ui/receipt-print-dialog'
import { Button } from '@/shared/ui/button'
import {
    calculateHighSeasonIncrementWithPeriods,
    calculateTotalPrice,
} from '@/shared/utils/pricing'
import { ClientClassification } from '@monorepo/functions/src/types/client'
import { HotelPricingConfig } from '@monorepo/functions/src/types/config'
import { type HairSalonReservation, type HotelReservation } from '@monorepo/functions/src/types/reservations'
import { type AdditionalService, type ServiceType } from '@monorepo/functions/src/types/services'

interface InvoiceModalProps {
    reservation: HotelReservation | HairSalonReservation
    onClose: () => void
    onPaid: () => void
    clientClassification?: ClientClassification
    /** @deprecated periods are loaded internally */
    config?: HotelPricingConfig
    /** When true, only print is shown (no Pay / Print and Pay). Use in reservation edit view. */
    printOnly?: boolean
    /** When true, generates a ticket for a hair salon reservation without hotel stay details. */
    isHairSalon?: boolean
}

interface VatItem {
    name: string
    quantity: number
    price: number
    vat: number
}

const distributeAmountInCents = (amount: number, totalParts: number, partIndex: number): number => {
    if (totalParts <= 0) return 0

    const totalCents = Math.round(amount * 100)
    const baseCents = Math.floor(totalCents / totalParts)
    const remainderCents = totalCents % totalParts
    const centsForPart = baseCents + (partIndex < remainderCents ? 1 : 0)

    return centsForPart / 100
}

const getHairdressingPetLabel = (
    reservation: HotelReservation | HairSalonReservation,
    petIndex: number,
): string => {
    if (reservation.type === 'hotel') {
        const pet = reservation.pets[petIndex]
        if (pet?.name) return pet.name.toUpperCase()
        if (pet?.size) return `PERRO ${pet.size.toUpperCase()}`
        return `PERRO ${petIndex + 1}`
    }

    if (petIndex === 0 && reservation.pet.name) {
        return reservation.pet.name.toUpperCase()
    }

    const clientPet = reservation.client.pets?.[petIndex]
    if (clientPet?.name) return clientPet.name.toUpperCase()
    if (clientPet?.size) return `PERRO ${clientPet.size.toUpperCase()}`

    return `PERRO ${petIndex + 1}`
}

const calculateAdditionalServicePrice = (service: AdditionalService, nights: number): number => {
    if (service.type === 'driver' || service.type === 'hairdressing') {
        return service.price || 0
    }

    return nights === 0 ? service.price || 0 : (service.price || 0) * nights
}

// Helper function to get service display name
const getServiceDisplayName = (type: ServiceType): string => {
    switch (type) {
        case 'hairdressing':
            return 'PELUQUERÍA'
        case 'special_care':
            return 'CUIDADOS ESP.'
        case 'special_food':
            return 'COMIDA ESP.'
        case 'medication':
            return 'MEDICACIÓN'
        case 'driver':
            return 'TRANSPORTE'
    }
}

export function InvoiceModal(props: InvoiceModalProps) {
    const {
        reservation,
        onClose,
        onPaid,
        clientClassification,
        config,
        printOnly = false,
        isHairSalon = false,
    } = props

    const { highSeasonPeriods } = useHotelAvailability()
    const hotelReservation = reservation.type === 'hotel' ? (reservation as HotelReservation) : null
    const { periods, loading: periodsLoading } = usePeriodsForReservation(
        hotelReservation?.checkInDate,
        hotelReservation?.checkOutDate,
    )

    const handlePrint = () => {
        window.print()
    }

    const handlePrintAndPay = () => {
        handlePrint()
        onPaid()
    }

    // Collect all items with their VAT rates
    const items: VatItem[] = []
    const vatRates = new Set<number>()
    const defaultVat = 21

    let total = 0

    if (isHairSalon && reservation.type === 'peluqueria') {
        const hairSalonReservation = reservation as HairSalonReservation
        total = hairSalonReservation.totalPrice ?? 0

        const hairdressingServices = hairSalonReservation.additionalServices.filter(
            service => service.type === 'hairdressing',
        )

        if (hairdressingServices.length <= 1) {
            const service = hairdressingServices[0]
            const petLabel = service
                ? getHairdressingPetLabel(hairSalonReservation, service.petIndex)
                : getHairdressingPetLabel(hairSalonReservation, 0)

            items.push({
                name: `PELUQUERÍA - ${petLabel}`,
                quantity: 1,
                price: service?.price || total,
                vat: defaultVat,
            })
            vatRates.add(defaultVat)
        } else {
            const hasIndividualPrices = hairdressingServices.some(service => (service.price ?? 0) > 0)

            hairdressingServices.forEach((service, index) => {
                const price = hasIndividualPrices
                    ? service.price || 0
                    : distributeAmountInCents(total, hairdressingServices.length, index)

                if (price <= 0) return

                items.push({
                    name: `PELUQUERÍA - ${getHairdressingPetLabel(hairSalonReservation, service.petIndex)}`,
                    quantity: 1,
                    price,
                    vat: defaultVat,
                })
                vatRates.add(defaultVat)
            })
        }
    } else {
        if (!hotelReservation) {
            throw new Error('Hotel reservation is required')
        }
        if (periodsLoading || !periods.length) {
            return (
                <div className='flex flex-col items-center gap-4 p-8'>
                    <p className='text-muted-foreground'>Cargando tarifas...</p>
                    <Button variant='outline' onClick={onClose}>
                        Cerrar
                    </Button>
                </div>
            )
        }

        const { total: hotelTotal, petsPriceByDay, nights } = calculateTotalPrice(
            hotelReservation,
            periods,
            clientClassification,
            highSeasonPeriods,
        )

        total = hotelTotal

        const totalStayPrice = nights === 0 ? petsPriceByDay : petsPriceByDay * nights
        const highSeasonIncrement = calculateHighSeasonIncrementWithPeriods(
            { from: new Date(hotelReservation.checkInDate), to: new Date(hotelReservation.checkOutDate) },
            periods,
            hotelReservation.pets.length,
            highSeasonPeriods,
        )
        const highSeasonPrice = highSeasonIncrement
        const stayBasePrice = totalStayPrice - highSeasonPrice

        const stayLabel = nights === 0 ? 'ESTANCIA (GUARDERÍA)' : `ESTANCIA (${nights} NOCHES)`
        const petsCount = hotelReservation.pets.length

        if (petsCount > 0) {
            hotelReservation.pets.forEach((pet, petIndex) => {
                items.push({
                    name: `${stayLabel} - PERRO ${pet.size.toUpperCase()}`,
                    quantity: 1,
                    price: distributeAmountInCents(stayBasePrice, petsCount, petIndex),
                    vat: defaultVat,
                })
                vatRates.add(defaultVat)
            })
        } else {
            items.push({
                name: stayLabel,
                quantity: 1,
                price: stayBasePrice,
                vat: defaultVat,
            })
            vatRates.add(defaultVat)
        }

        if (highSeasonPrice > 0) {
            items.push({
                name: 'SUPLEMENTO TEMPORADA ALTA',
                quantity: 1,
                price: highSeasonPrice,
                vat: defaultVat,
            })
            vatRates.add(defaultVat)
        }

        // Add additional services (hairdressing is listed per pet, other services are grouped by type)
        const servicesByType = new Map<string, { name: string; totalPrice: number; count: number }>()

        hotelReservation.additionalServices.forEach(service => {
            const price = calculateAdditionalServicePrice(service, nights)

            if (price <= 0) return

            if (service.type === 'hairdressing') {
                items.push({
                    name: `PELUQUERÍA - ${getHairdressingPetLabel(hotelReservation, service.petIndex)}`,
                    quantity: 1,
                    price,
                    vat: defaultVat,
                })
                vatRates.add(defaultVat)
                return
            }

            const serviceName = getServiceDisplayName(service.type)
            const existing = servicesByType.get(serviceName)
            if (existing) {
                existing.totalPrice += price
                existing.count += 1
            } else {
                servicesByType.set(serviceName, {
                    name: serviceName,
                    totalPrice: price,
                    count: 1,
                })
            }
        })

        // Add grouped services to items
        servicesByType.forEach(serviceData => {
            items.push({
                name: serviceData.count > 1 ? `${serviceData.name} (x${serviceData.count})` : serviceData.name,
                quantity: serviceData.count,
                price: serviceData.totalPrice,
                vat: defaultVat,
            })
            vatRates.add(defaultVat)
        })

        // Add extras
        hotelReservation.extras?.forEach(extra => {
            items.push({
                name: extra.concept.toUpperCase(),
                quantity: 1,
                price: extra.amount,
                vat: defaultVat,
            })
            vatRates.add(defaultVat)
        })

        // Add shop products
        hotelReservation.shopProducts?.forEach(product => {
            const vat = product.vat || defaultVat
            items.push({
                name: product.name.toUpperCase(),
                quantity: product.quantity,
                price: product.totalPrice,
                vat: vat,
            })
            vatRates.add(vat)
        })
    }

    // Calculate totals by VAT rate
    const vatTotals = Array.from(vatRates).map(rate => {
        const itemsWithThisVat = items.filter(item => item.vat === rate)
        const subtotal = itemsWithThisVat.reduce((sum, item) => sum + item.price, 0)
        return {
            rate,
            subtotal,
            vatAmount: subtotal - subtotal / (1 + rate / 100),
        }
    })

    const receiptItems = items.map(item => ({
        name: item.name,
        quantity: item.quantity,
        price: item.price,
    }))
    const baseAmount = total - vatTotals.reduce((sum, vat) => sum + vat.vatAmount, 0)

    return (
        <ReceiptPrintDialog
            title='Factura Simplificada'
            description='Vista previa del ticket'
            documentLabel='FACTURA SIMPLIFICADA'
            items={receiptItems}
            vatTotals={vatTotals}
            baseAmount={baseAmount}
            total={total}
            onClose={onClose}
            actions={handlePrint => (
                <>
                    <Button variant='outline' onClick={handlePrint} className='gap-2'>
                        <Printer className='h-4 w-4' />
                        Imprimir
                    </Button>
                    {!printOnly && (
                        <>
                            <Button variant='outline' onClick={onPaid} className='gap-2'>
                                <Check className='h-4 w-4' />
                                Pagado
                            </Button>
                            <Button onClick={handlePrintAndPay} className='gap-2 bg-black'>
                                <CreditCard className='h-4 w-4' />
                                Imprimir y Pagar
                            </Button>
                        </>
                    )}
                </>
            )}
        />
    )
}
